import { zodResolver } from '@hookform/resolvers/zod';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { useAuth } from '@/lib/auth/useAuth';
import { useLandAfterAuth } from '@/lib/auth/useLandAfterAuth';
import { supabase } from '@/lib/supabase';

// Reset completion. A recovery link routes here from the callback with an
// established session; the user sets a new password (sign-up's rules), then
// lands through the normal precedence chain. Never a blank form: no session on
// arrival means the link expired, and we say so.
const schema = z
  .object({
    password: z
      .string()
      .min(8, 'Use at least 8 characters')
      .regex(/[A-Z]/, 'Include at least one uppercase letter')
      .regex(/[0-9]/, 'Include at least one number'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
type Values = z.infer<typeof schema>;

export const Route = createFileRoute('/auth/update-password')({
  component: UpdatePasswordRoute,
});

function UpdatePasswordRoute() {
  const { session, loading } = useAuth();
  const land = useLandAfterAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    // updateUser surfaces its own errors here: a same-as-current password
    // ("New password should be different…") or a session that lapsed mid-form.
    const { error } = await supabase.auth.updateUser({ password: values.password });
    if (error) {
      setSubmitError(error.message);
      return;
    }
    setDone(true);
  });

  if (loading) return null;

  // Session guard — a recovery link establishes the session in the callback, so
  // no session here means the link expired or was opened cold.
  if (!session) {
    return (
      <AuthLayout
        title="That link has expired"
        subtitle="Password-reset links are single-use and time-limited. Request a fresh one."
      >
        <Link
          to="/auth/reset-password"
          className="block w-full rounded-md bg-ink px-4 py-2.5 text-center font-sans text-sm font-medium text-parchment hover:bg-oxblood"
        >
          Request a new reset link
        </Link>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Password updated" subtitle="Your new password is set.">
        <button
          type="button"
          onClick={() => void land(null)}
          className="w-full rounded-md bg-ink px-4 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood"
        >
          Continue
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set a new password" subtitle="Choose a new password for your account.">
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <Field
          id="password"
          label="New password"
          type="password"
          autoComplete="new-password"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />
        <Field
          id="confirmPassword"
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          error={form.formState.errors.confirmPassword?.message}
          {...form.register('confirmPassword')}
        />
        {submitError && (
          <p role="alert" aria-live="polite" className="text-sm text-signal">
            {submitError}
          </p>
        )}
        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="w-full rounded-md bg-ink px-4 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
        >
          {form.formState.isSubmitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </AuthLayout>
  );
}

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string;
}
const Field = (() => {
  const C = ({ id, label, error, ...rest }: FieldProps) => (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60"
      >
        {label}
      </label>
      <input
        id={id}
        {...rest}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
      />
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-signal">
          {error}
        </p>
      )}
    </div>
  );
  return C;
})();
